import {
  addToaster,
  DropdownType,
  getGenericEntityListingAPI,
  getRecipientsByEntityInstanceId,
  getRecipientsCorrespondingToEntityInstance,
  hideLoader,
  OrganizationOptions,
  RecipientFaxOutbound,
  showLoader,
} from '@app-store'
import {
  ControlledDropDown,
  CustomButton,
  FORMAT_PATTERN,
  formatPhoneNumber,
  MultiLineInput,
} from '@ui-kit'
import {
  COMM_TEMPLATE,
  ENTITY_NAME,
  NAMESPACES,
  PHONE_TYPE_CATEGORY_VALUES,
  TASK_STATUS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { STRINGS } from '../../utils/strings'
import { FieldValues, useForm } from 'react-hook-form'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { FC, useEffect, useState } from 'react'
import {
  formatNumberTwoDigits,
  getEntityName,
  logErrorMessage,
  setPhoneTypeIdFunction,
} from '../../utils/helperFunctions'
import { uniqueId } from 'lodash'
import { editEntityDataEmbeddedAPI } from '../../apis/entityData.api'
import { CallOutboundTaskProps } from '../../types/workflow.types'
import { currentUserId } from '../../utils/permission'

const CallOutboundTask: FC<CallOutboundTaskProps> = ({
  task,
  tabIndexOffset,
  isDisabled = false,
  updateTaskResource,
}) => {
  const dispatch = useAppDispatch()
  const [notesState, setNotesState] = useState<boolean>(false)
  const [taskNotesData, setTaskNotesData] = useState<FieldValues>()
  const { setValue, register, getValues, watch, control } = useForm()
  const [taskNoteBoxId, setTaskNoteBoxId] = useState<string | undefined>()
  const workflowDefinition = useAppSelector(
    (state: RootState) => state?.workflowData?.workflowDefinition
  )
  const mode = notesState || !taskNotesData ? 'edit' : 'view'

  const globalVariables = useAppSelector(
    (state: RootState) =>
      state?.workflowData?.workflowDefinition?.global_variables
  )
  const [toSmsFax, setToSmsFax] = useState<DropdownType<string>[]>([])

  useEffect(() => {
    setValue('to', '')

    const fetchRecipientOptions = async () => {
      const recordId =
        task?.requestor_view?.record_id ?? task?.organization_view?.record_id
      const entityType =
        task?.requestor_view?.entity_type ??
        (task?.organization_view as OrganizationOptions)?.organization_type
      if (recordId) {
        const entityName = getEntityName(entityType ?? '')
        const entityPayload =
          globalVariables?.[entityName as 'contacts' | 'patients' | 'providers']
        const updatedPhonePayload = setPhoneTypeIdFunction(
          entityPayload ?? {
            select: [],
            entity_name: '',
            related_entities: {},
          },
          PHONE_TYPE_CATEGORY_VALUES.PHONE
        )
        const payload: getRecipientsByEntityInstanceId = {
          id: recordId,
          apiPayload: updatedPhonePayload ?? {
            select: [],
            entity_name: '',
            related_entities: {},
          },
        }
        const relatedEntityName = payload?.apiPayload?.select?.[0]
        const response = await getRecipientsCorrespondingToEntityInstance(
          payload
        )
        return response?.data?.[relatedEntityName]
      }
    }
    ;(async () => {
      const recipientResponse = await fetchRecipientOptions()
      const recipientOptions = recipientResponse?.map(
        (item: RecipientFaxOutbound) => ({
          label: item?.is_preferred
            ? formatPhoneNumber(item.phone_number, FORMAT_PATTERN.PHONE_INPUT) +
              ' (Primary)'
            : formatPhoneNumber(item?.phone_number, FORMAT_PATTERN.PHONE_INPUT),
          value: formatPhoneNumber(
            item.phone_number,
            FORMAT_PATTERN.PHONE_INPUT
          ),
        })
      )
      setToSmsFax(recipientOptions)
    })()
  }, [task?.requestor_view?.record_id, task?.organization_view?.record_id])

  const fetchTaskNotesData = async () => {
    try {
      dispatch(showLoader())
      const payload = {
        entity_name:
          workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        select: ['c_case_notes_task', NAMESPACES.NAME_KEY, 'header'],
        filters: [{ criteria: [] }],
        related_entities: {
          c_case_notes_task: {
            select: [NAMESPACES.NAME_KEY],
            criteria: [{ criteria: [] }],
          },
        },
      }
      const responseData = await getGenericEntityListingAPI(payload, task?.id)
      setTaskNotesData(responseData?.c_case_notes_task?.[0]?.name)
      if (responseData?.c_case_notes_task?.[0]?.id)
        setTaskNoteBoxId(
          responseData?.c_case_notes_task?.[0]?.id ?? task?.notes
        )
      setValue(
        `taskNotes${task?.task_id}`,
        responseData?.c_case_notes_task?.[0]?.name ?? task?.notes
      )
    } catch (error) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      logErrorMessage('taskNotes', error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const updateTask = async () => {
    try {
      dispatch(showLoader())
      await editEntityDataEmbeddedAPI(
        workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        task?.id ?? '',
        {
          c_case_notes_task: [
            {
              name: getValues(`taskNotes${task?.task_id}`),
              source: NAMESPACES.TASK,
              headers: `T${formatNumberTwoDigits(
                task?.task_id ? +task?.task_id : 0
              )} - ${task?.name} - ${
                task?.requestor_view?.name ??
                task?.organization_view?.organization_name ??
                ''
              } - Phone no. (${formatPhoneNumber(
                watch('phone_number'),
                FORMAT_PATTERN.PHONE_INPUT
              )})`,
              ...(taskNoteBoxId ? { id: taskNoteBoxId } : {}),
              _state: NAMESPACES.CHANGED,
              case: {
                id: task?.case_id,
                _state: NAMESPACES.CHANGED,
              },
              ...(!taskNoteBoxId ? { users_id: currentUserId() ?? '' } : {}),
            },
          ],
          record_status: TASK_STATUS.ACTIVE,
        },
        ['c_case_notes_task']
      )
      dispatch(
        addToaster({
          message: NAMESPACES.TASK_NOTES_SAVED,
          type: 'success',
          id: uniqueId(),
        })
      )
      await updateTaskResource?.(task, {
        resource_id: formatPhoneNumber(
          watch('phone_number'),
          FORMAT_PATTERN.PHONE_INPUT
        ),
        resource:
          getValues(`taskNotes${task?.task_id}`) !== ''
            ? getValues(`taskNotes${task?.task_id}`)
            : '',
      })
    } catch (error) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      logErrorMessage('taskNotes', error)
    } finally {
      setNotesState(false)
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    if (workflowDefinition && task) {
      fetchTaskNotesData()
    }
  }, [workflowDefinition, task])

  useEffect(() => {
    if (task?.resource_id) {
      setValue('phone_number', task?.resource_id)
    }
  }, [task])

  useEffect(() => {
    setValue('to', '')

    const fetchRecipientOptions = async () => {
      const recordId =
        task?.requestor_view?.record_id ?? task?.organization_view?.record_id
      const entityType =
        task?.requestor_view?.entity_type ??
        (task?.organization_view as OrganizationOptions)?.organization_type
      if (recordId) {
        const entityName = getEntityName(entityType ?? '')
        const basePayload =
          globalVariables?.[entityName as 'contacts' | 'patients' | 'providers']

        if (!basePayload) return
        const entityPayload = {
          ...basePayload,
          select:
            entityName === ENTITY_NAME.PATIENTS
              ? [NAMESPACES.C_PATIENT_PHONE]
              : basePayload?.select,
        }
        const updatedPhonePayload = setPhoneTypeIdFunction(
          entityPayload ?? {
            select: [],
            entity_name: '',
            related_entities: {},
          },
          PHONE_TYPE_CATEGORY_VALUES.PHONE
        )
        const payload: getRecipientsByEntityInstanceId = {
          id: recordId,
          apiPayload: updatedPhonePayload ?? {
            select: [],
            entity_name: '',
            related_entities: {},
          },
        }
        let relatedEntityName
        if (entityName === 'patients') {
          relatedEntityName = NAMESPACES.C_PATIENT_PHONE
        } else {
          relatedEntityName = payload?.apiPayload?.select?.[0]
        }
        const response = await getRecipientsCorrespondingToEntityInstance(
          payload
        )
        return response?.data?.[relatedEntityName]
      }
    }
    ;(async () => {
      const recipientResponse = await fetchRecipientOptions()
      const recipientOptions = recipientResponse?.map(
        (item: RecipientFaxOutbound) => ({
          label: item?.is_preferred
            ? formatPhoneNumber(item.phone_number, FORMAT_PATTERN.PHONE_INPUT) +
              ' (Primary)'
            : formatPhoneNumber(item?.phone_number, FORMAT_PATTERN.PHONE_INPUT),
          value: formatPhoneNumber(
            item.phone_number,
            FORMAT_PATTERN.PHONE_INPUT
          ),
        })
      )
      setToSmsFax(recipientOptions)
    })()
  }, [task?.requestor_view?.record_id, task?.organization_view?.record_id])

  return (
    <div className="p-2 mt-2">
      <div>
        <div className="flex justify-between items-center">
          <div className="flex w-[28rem]">
            <ControlledDropDown
              options={toSmsFax}
              name="phone_number"
              label={COMM_TEMPLATE.PHONE_NO}
              isSearchable={false}
              control={control}
              required={true}
              onChange={(value: DropdownType<string>) => {
                setValue('to', value?.value, { shouldValidate: true })
              }}
              dataQaId={'to'}
              disabled={mode === 'view' || isDisabled}
              key={task?.resource_id}
            />
          </div>
          <div className="flex flex-row-reverse pb-2 gap-2">
            {!notesState && taskNotesData ? (
              <div className="flex justify-between items-center">
                <CustomButton
                  isFilled
                  disabled={task?.state !== WORKFLOW_STATUS.OPEN || isDisabled}
                  color="primary"
                  onClick={() => setNotesState(true)}
                  tabIndex={tabIndexOffset}
                >
                  {STRINGS.EDIT}
                </CustomButton>
              </div>
            ) : (
              <CustomButton
                isFilled
                color="primary"
                onClick={updateTask}
                tabIndex={tabIndexOffset}
                disabled={
                  !watch('phone_number') ||
                  task?.state !== WORKFLOW_STATUS.OPEN ||
                  isDisabled
                }
              >
                {STRINGS.SAVE}
              </CustomButton>
            )}
            {mode !== 'view' ? (
              <CustomButton
                disabled={
                  !watch(`taskNotes${task?.task_id}`)?.trim() ||
                  task?.state !== WORKFLOW_STATUS.OPEN ||
                  isDisabled
                }
                color="primary"
                onClick={() => setValue(`taskNotes${task?.task_id}`, '')}
                tabIndex={tabIndexOffset}
              >
                {STRINGS.CLEAR}
              </CustomButton>
            ) : null}
          </div>
        </div>
        <div className="grid col-span-3 mt-4">
          <MultiLineInput
            className="w-full border rounded p-4 min-h-20 overflow-y-auto"
            name={`taskNotes${task?.task_id}`}
            widthClass="w-full"
            disabled={mode === 'view' || isDisabled}
            register={register}
            id="editing-note"
            label="Notes"
          />
        </div>
      </div>
    </div>
  )
}

export default CallOutboundTask
